import type { Account, AccountCreate, AccountType, AccountUpdate, TrialBalanceRow } from '@erp/shared';
import { Ban, BookOpen, ChevronDown, Pencil, RotateCcw, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Combobox, type ComboboxOption, confirmDialog, EmptyState, FormField } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatMoney } from '../../lib/money.js';

const TYPE_ORDER: AccountType[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];

/** Account type → a shell-bridge CSS var, for the type badge + filter dot. */
const TYPE_VAR: Record<AccountType, string> = {
  asset: '--viz-1',
  liability: '--danger',
  equity: '--accent',
  revenue: '--success',
  expense: '--info',
};

interface CoaRow {
  account: Account;
  depth: number;
  hasChildren: boolean;
}

/** Index accounts by parent id; each child list sorted by code. */
function indexChildren(accounts: Account[]): Map<string | null, Account[]> {
  const byParent = new Map<string | null, Account[]>();
  for (const a of accounts) {
    const key = a.parentId ?? null;
    const list = byParent.get(key);
    if (list) list.push(a);
    else byParent.set(key, [a]);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.code.localeCompare(b.code));
  return byParent;
}

function subtreeMatches(a: Account, byParent: Map<string | null, Account[]>, q: string): boolean {
  if (a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q)) return true;
  return (byParent.get(a.id) ?? []).some((c) => subtreeMatches(c, byParent, q));
}

/**
 * Flatten the account tree into rows in CoA order (by type, then code),
 * applying the active type filter, the search query (which auto-expands
 * matching branches), and the collapsed set.
 */
function flattenCoa(
  byParent: Map<string | null, Account[]>,
  filter: AccountType | 'all',
  query: string,
  collapsed: ReadonlySet<string>,
): CoaRow[] {
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const out: CoaRow[] = [];
  const walk = (a: Account, depth: number): void => {
    if (searching && !subtreeMatches(a, byParent, q)) return;
    const kids = byParent.get(a.id) ?? [];
    out.push({ account: a, depth, hasChildren: kids.length > 0 });
    if (kids.length > 0 && !(collapsed.has(a.id) && !searching)) {
      for (const k of kids) walk(k, depth + 1);
    }
  };
  const roots = byParent.get(null) ?? [];
  for (const type of TYPE_ORDER) {
    if (filter !== 'all' && filter !== type) continue;
    for (const r of roots.filter((a) => a.accountType === type)) walk(r, 0);
  }
  return out;
}

export function AccountsPage(): JSX.Element {
  const { t } = useTranslation('accounts');
  const { t: tc } = useTranslation();
  const tenant = useAuthStore((s) => s.tenant);
  const canWrite = usePermission('accounts.write');
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<AccountType | 'all'>('all');
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const { i18n } = useTranslation();
  // Balances ride on the ledger permission: the chart itself is accounts.read,
  // a branch keeper can hold that without seeing the company's books.
  const canSeeBalances = usePermission('gl.read');
  const [tb, setTb] = useState<Map<string, TrialBalanceRow> | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Account[] }>('/accounts');
    setAccounts(r.data.items);
    if (canSeeBalances) {
      void api().get<{ items: TrialBalanceRow[] }>('/general-ledger/trial-balance')
        .then((b) => setTb(new Map(b.data.items.map((x) => [x.accountId, x]))))
        .catch(() => setTb(null));
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const byParent = useMemo(() => indexChildren(accounts ?? []), [accounts]);
  const rows = useMemo(
    () => flattenCoa(byParent, filter, query, collapsed),
    [byParent, filter, query, collapsed],
  );
  const parentIds = useMemo(
    () => (accounts ?? []).filter((a) => (byParent.get(a.id)?.length ?? 0) > 0).map((a) => a.id),
    [accounts, byParent],
  );
  const allCollapsed = parentIds.length > 0 && parentIds.every((pid) => collapsed.has(pid));

  // Each account's balance per currency, its sub-accounts rolled in — a group
  // row says what the whole branch of the chart holds, in dinars AND dollars.
  const rolled = useMemo(() => {
    const out = new Map<string, { byCcy: Map<string, number>; base: number }>();
    if (!tb) return out;
    const walk = (id: string): { byCcy: Map<string, number>; base: number } => {
      const hit = out.get(id);
      if (hit) return hit;
      const own = tb.get(id);
      const byCcy = new Map<string, number>();
      for (const c of own?.byCurrency ?? []) byCcy.set(c.currencyCode, (byCcy.get(c.currencyCode) ?? 0) + Number(c.balance));
      let base = Number(own?.baseBalance ?? 0);
      for (const child of byParent.get(id) ?? []) {
        const r = walk(child.id);
        for (const [k, v] of r.byCcy) byCcy.set(k, (byCcy.get(k) ?? 0) + v);
        base += r.base;
      }
      const v = { byCcy, base };
      out.set(id, v);
      return v;
    };
    for (const a of accounts ?? []) walk(a.id);
    return out;
  }, [tb, byParent, accounts]);
  // The currencies anything was posted in — base first — one column each.
  const balanceCcys = useMemo(() => {
    const set = new Set<string>();
    for (const r of tb?.values() ?? []) for (const c of r.byCurrency ?? []) set.add(c.currencyCode);
    const base = tenant?.baseCurrencyCode ?? '';
    return [...set].sort((a, b) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)));
  }, [tb, tenant?.baseCurrencyCode]);
  const showBalances = canSeeBalances && tb !== null;
  const bal = (v: number | undefined, ccy: string): string =>
    v && Math.abs(v) > 0.00005 ? formatMoney(v.toFixed(4), { locale: i18n.language, currency: ccy }) : '—';
  const editing = editingId && accounts ? accounts.find((a) => a.id === editingId) ?? null : null;

  if (!accounts) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';

  const onDeactivate = async (account: Account): Promise<void> => {
    const message = t('actions.confirmDeactivate', { code: account.code, name: account.name });
    if (!(await confirmDialog({ message, tone: 'danger' }))) return;
    try {
      await api().delete(`/accounts/${account.id}`);
      await reload();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict' ? t('errors.hasActiveChildren') : tc('errors:server'));
    }
  };

  const onReactivate = async (account: Account): Promise<void> => {
    const message = t('actions.confirmReactivate', { code: account.code, name: account.name });
    if (!(await confirmDialog({ message }))) return;
    try {
      // AccountUpdateSchema accepts `isActive: boolean`; flipping to true
      // brings the row back into the active set. The hasActiveChildren
      // guard is only enforced on DELETE — reactivating a leaf or a
      // dormant parent both go through the same PATCH.
      await api().patch(`/accounts/${account.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const toggle = (acc: Account): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(acc.id)) next.delete(acc.id);
      else next.add(acc.id);
      return next;
    });
  const toggleAll = (): void => setCollapsed(allCollapsed ? new Set() : new Set(parentIds));

  const FILTERS: (AccountType | 'all')[] = ['all', ...TYPE_ORDER];
  const filterLabel = (f: AccountType | 'all'): string =>
    f === 'all' ? t('list.filterAll', { defaultValue: 'All' }) : t(`type.${f}`);

  return (
    <div className="coa-view flex flex-col gap-4">
      <style>{COA_CSS}</style>
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('description')}</p>
        </div>
        <div className="flex items-center gap-2">
          {accounts.length > 0 && (
            <Button variant="secondary" onClick={toggleAll}>
              {allCollapsed
                ? t('list.expandAll', { defaultValue: 'Expand all' })
                : t('list.collapseAll', { defaultValue: 'Collapse all' })}
            </Button>
          )}
          {canWrite && !showCreate && !editingId && (
            <Button onClick={() => setShowCreate(true)}>{t('create.title')}</Button>
          )}
        </div>
      </header>

      {showCreate && (
        <CreateAccountForm
          accounts={accounts}
          baseCurrency={baseCurrency}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditAccountForm
          account={editing}
          accounts={accounts}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            setEditingId(null);
            await reload();
          }}
        />
      )}

      {accounts.length === 0 && !showCreate ? (
        <EmptyState
          icon={<BookOpen className="h-8 w-8" aria-hidden />}
          title={t('list.empty')}
          description={t('list.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="coa-toolbar">
            <div className="coa-filters">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`fchip${filter === f ? ' active' : ''}`}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f !== 'all' && (
                    <span className="dot" style={{ background: `var(${TYPE_VAR[f]})` }} />
                  )}
                  {filterLabel(f)}
                </button>
              ))}
            </div>
            <label className="coa-search">
              <Search className="h-4 w-4 flex-none" aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('list.searchPlaceholder', { defaultValue: 'Search account or code…' })}
                aria-label={t('list.searchPlaceholder', { defaultValue: 'Search account or code…' })}
              />
            </label>
          </div>

          <Card className="overflow-hidden p-0">
            <div className="coa-scroll">
              <table className="coa">
                <thead>
                  <tr>
                    <th>{t('list.columns.code')}</th>
                    <th>{t('list.columns.name')}</th>
                    <th>{t('list.columns.type')}</th>
                    <th>{t('list.columns.currency')}</th>
                    {showBalances && balanceCcys.map((c) => (
                      <th key={c} className="num">{t('list.columns.balanceIn', { ccy: c })}</th>
                    ))}
                    {showBalances && balanceCcys.length > 1 && (
                      <th className="num">{t('list.columns.baseTotal', { ccy: baseCurrency })}</th>
                    )}
                    <th>{t('list.columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ account: a, depth, hasChildren }) => {
                    const isCollapsed = collapsed.has(a.id);
                    const groupCls = hasChildren ? (depth === 0 ? ' row-group' : ' row-sub') : '';
                    return (
                      <tr key={a.id} className={`coa-row${groupCls}`}>
                        <td>
                          <div className="cell">
                            <span className="code">{a.code}</span>
                          </div>
                        </td>
                        <td>
                          <div className="cell" style={{ paddingInlineStart: `${12 + depth * 20}px` }}>
                            <span className={`acc-name${hasChildren && isCollapsed ? ' collapsed' : ''}`}>
                              {hasChildren ? (
                                <button
                                  type="button"
                                  className="chev"
                                  onClick={() => toggle(a)}
                                  aria-expanded={!isCollapsed}
                                  aria-label={tc(isCollapsed ? 'actions.expand' : 'actions.collapse', {
                                    defaultValue: isCollapsed ? 'Expand' : 'Collapse',
                                  })}
                                >
                                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                                </button>
                              ) : (
                                <span className="chev chev-spacer" aria-hidden />
                              )}
                              <Link to={`/accounting/accounts/${a.id}`} className="nm text-fg no-underline transition-colors hover:text-brand hover:underline">{a.name}</Link>
                              {canWrite && (
                                <span className="acc-actions">
                                  <button
                                    type="button"
                                    onClick={() => setEditingId(a.id)}
                                    aria-label={`${tc('actions.edit')} ${a.code}`}
                                    title={tc('actions.edit')}
                                  >
                                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                                  </button>
                                  {a.isActive ? (
                                    <button
                                      type="button"
                                      onClick={() => void onDeactivate(a)}
                                      aria-label={`${t('actions.deactivate')} ${a.code}`}
                                      title={t('actions.deactivate')}
                                    >
                                      <Ban className="h-3.5 w-3.5" aria-hidden />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => void onReactivate(a)}
                                      aria-label={`${t('actions.reactivate')} ${a.code}`}
                                      title={t('actions.reactivate')}
                                    >
                                      <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                                    </button>
                                  )}
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="cell">
                            {depth === 0 ? (
                              <span
                                className="tbadge"
                                style={{
                                  background: `color-mix(in oklab, var(${TYPE_VAR[a.accountType]}) 16%, transparent)`,
                                  color: `var(${TYPE_VAR[a.accountType]})`,
                                }}
                              >
                                {t(`type.${a.accountType}`)}
                              </span>
                            ) : (
                              <span className="dash">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="cell">
                            <span className="cur-tag">{a.currencyCode}</span>
                          </div>
                        </td>
                        {showBalances && balanceCcys.map((c) => (
                          <td key={c} className="num">
                            <div className="cell" dir="ltr">{bal(rolled.get(a.id)?.byCcy.get(c), c)}</div>
                          </td>
                        ))}
                        {showBalances && balanceCcys.length > 1 && (
                          <td className="num">
                            <div className="cell base" dir="ltr">{bal(rolled.get(a.id)?.base, baseCurrency)}</div>
                          </td>
                        )}
                        <td>
                          <div className="cell">
                            <span className={`status-pill ${a.isActive ? 'on' : 'off'}`}>
                              {t(`status.${a.isActive ? 'active' : 'inactive'}`)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5 + (showBalances ? balanceCcys.length + (balanceCcys.length > 1 ? 1 : 0) : 0)}>
                        <div className="coa-empty">
                          {t('list.noMatches', { defaultValue: 'No accounts match your filters.' })}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

interface CreateAccountFormProps {
  accounts: Account[];
  baseCurrency: string;
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}

function CreateAccountForm({
  accounts,
  baseCurrency,
  onCancel,
  onCreated,
}: CreateAccountFormProps): JSX.Element {
  const { t } = useTranslation('accounts');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<AccountCreate>({
    code: '',
    name: '',
    accountType: 'asset',
    currencyCode: baseCurrency,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const eligibleParents = accounts.filter(
    (a) => a.accountType === form.accountType && a.isActive,
  );

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload: AccountCreate = {
        code: form.code.trim(),
        name: form.name.trim(),
        accountType: form.accountType,
        currencyCode: form.currencyCode,
      };
      if (form.accountSubtype) payload.accountSubtype = form.accountSubtype;
      if (form.parentId) payload.parentId = form.parentId;
      await api().post('/accounts', payload);
      await onCreated();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      switch (code) {
        case 'conflict':
          setError(t('errors.duplicateCode'));
          break;
        case 'unprocessable':
          setError(t('errors.parentTypeMismatch'));
          break;
        default:
          setError(tc('errors:server'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('create.title')}</h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="acc-code" label={t('create.code')} required>
          <Input
            id="acc-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="acc-name" label={t('create.name')} required>
          <Input
            id="acc-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </FormField>
        <FormField id="acc-type" label={t('create.type')} required>
          <select
            id="acc-type"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.accountType}
            onChange={(e) =>
              setForm({
                ...form,
                accountType: e.target.value as AccountType,
                parentId: undefined,
              })
            }
          >
            {TYPE_ORDER.map((type) => (
              <option key={type} value={type}>
                {t(`type.${type}`)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="acc-subtype" label={t('create.subtypeOptional')}>
          <Input
            id="acc-subtype"
            value={form.accountSubtype ?? ''}
            onChange={(e) => setForm({ ...form, accountSubtype: e.target.value })}
          />
        </FormField>
        <FormField id="acc-parent" label={t('create.parent')}>
          <Combobox
            id="acc-parent"
            options={[{ value: '__none__', label: t('create.parentNone') }, ...eligibleParents.map((p): ComboboxOption => ({ value: p.id, label: `${p.code} — ${p.name}`, keywords: `${p.code} ${p.name}` }))]}
            value={form.parentId ?? undefined}
            onChange={(v) => setForm({ ...form, parentId: v && v !== '__none__' ? v : undefined })}
            placeholder={t('create.parentNone')}
            ariaLabel={t('create.parent')}
          />
        </FormField>
        <FormField id="acc-currency" label={t('create.currency')} required>
          <CurrencySelect
            id="acc-currency"
            value={form.currencyCode}
            onChange={(c) => setForm({ ...form, currencyCode: c })}
            required
          />
        </FormField>
        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {t('create.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('create.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface EditAccountFormProps {
  account: Account;
  accounts: Account[];
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function EditAccountForm({
  account,
  accounts,
  onCancel,
  onSaved,
}: EditAccountFormProps): JSX.Element {
  const { t } = useTranslation('accounts');
  const { t: tc } = useTranslation();
  const [name, setName] = useState(account.name);
  const [subtype, setSubtype] = useState(account.accountSubtype ?? '');
  const [parentId, setParentId] = useState<string | ''>(account.parentId ?? '');
  const [isActive, setIsActive] = useState(account.isActive);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Eligible new parents: same type, active, and not the account itself.
  const eligibleParents = accounts.filter(
    (a) => a.accountType === account.accountType && a.isActive && a.id !== account.id,
  );

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload: AccountUpdate = {
        name: name.trim(),
        accountSubtype: subtype.trim() || null,
        parentId: parentId === '' ? null : parentId,
        isActive,
      };
      await api().patch(`/accounts/${account.id}`, payload);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      switch (code) {
        case 'unprocessable':
          setError(t('errors.parentTypeMismatch'));
          break;
        default:
          setError(tc('errors:server'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('edit.title', { code: account.code, name: account.name })}
      </h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="ed-code" label={t('create.code')}>
          <Input id="ed-code" value={account.code} disabled />
        </FormField>
        <FormField id="ed-name" label={t('create.name')} required>
          <Input id="ed-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </FormField>
        <FormField id="ed-type" label={t('create.type')}>
          <Input id="ed-type" value={t(`type.${account.accountType}`)} disabled />
        </FormField>
        <FormField id="ed-subtype" label={t('create.subtypeOptional')}>
          <Input id="ed-subtype" value={subtype} onChange={(e) => setSubtype(e.target.value)} />
        </FormField>
        <FormField id="ed-parent" label={t('create.parent')}>
          <Combobox
            id="ed-parent"
            options={[{ value: '__none__', label: t('create.parentNone') }, ...eligibleParents.map((p): ComboboxOption => ({ value: p.id, label: `${p.code} — ${p.name}`, keywords: `${p.code} ${p.name}` }))]}
            value={parentId || undefined}
            onChange={(v) => setParentId(v && v !== '__none__' ? v : '')}
            placeholder={t('create.parentNone')}
            ariaLabel={t('create.parent')}
          />
        </FormField>
        <FormField id="ed-active" label={t('list.columns.status')}>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              id="ed-active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>{t(`status.${isActive ? 'active' : 'inactive'}`)}</span>
          </label>
        </FormField>
        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {tc('actions.save')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('create.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* Scoped Chart-of-Accounts tree styles. Uses the shell token bridge
   (--surface, --primary, --viz-N, …) so the tree tracks the active theme
   and palette. */
const COA_CSS = `
.coa-view .coa-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.coa-view .coa-filters { display: flex; gap: 8px; flex-wrap: wrap; }
.coa-view .fchip { display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 14px; border-radius: var(--r-pill); border: 1px solid var(--border); background: var(--surface); color: var(--text-muted); font-weight: 600; font-size: 13px; cursor: pointer; transition: color .12s, background .12s; }
.coa-view .fchip:hover { color: var(--text); }
.coa-view .fchip .dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.coa-view .fchip.active { background: var(--text); color: var(--bg); border-color: transparent; }
[data-theme='dark'] .coa-view .fchip.active, .dark .coa-view .fchip.active { background: var(--surface-2); color: var(--text); border-color: var(--border-strong); }
.coa-view .coa-search { margin-inline-start: auto; display: inline-flex; align-items: center; gap: 8px; height: 34px; flex: 0 1 280px; min-width: 160px; padding: 0 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-md); color: var(--text-subtle); }
.coa-view .coa-search input { border: 0; background: transparent; outline: none; color: var(--text); font: inherit; font-size: var(--fs-sm); flex: 1; min-width: 0; }

.coa-view .coa-scroll { max-height: calc(var(--vh100) - 17rem); overflow: auto; }
.coa-view table.coa { width: 100%; border-collapse: collapse; }
.coa-view .coa thead th { position: sticky; top: 0; z-index: 2; background: var(--surface); text-align: start; font-size: var(--fs-over); font-weight: 700; letter-spacing: var(--tracking-over); text-transform: uppercase; color: var(--text-subtle); padding: 11px var(--s-4); border-bottom: 1px solid var(--border); white-space: nowrap; }
[dir='rtl'] .coa-view .coa thead th { letter-spacing: 0; }
.coa-view .coa td { padding: 0; border-bottom: 1px solid var(--border-soft); font-size: var(--fs-sm); color: var(--text); }
.coa-view .coa td .cell { padding: 10px var(--s-4); }
/* Balance columns: one per currency, then the base total — figures, not text. */
.coa-view .coa th.num { text-align: end; }
.coa-view .coa td.num .cell { text-align: end; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
.coa-view .coa td.num .cell.base { color: var(--text-subtle); font-weight: 500; }
.coa-view .coa tr.row-group td.num .cell { font-weight: 800; }
.coa-view .coa .code { font-family: var(--font-num); color: var(--text-subtle); font-weight: 600; }
.coa-view .dash { color: var(--text-subtle); }

.coa-view .acc-name { display: flex; align-items: center; gap: 8px; }
.coa-view .acc-name .chev { width: 18px; height: 18px; flex: none; display: grid; place-items: center; color: var(--text-subtle); transition: transform .15s, background .12s, color .12s; cursor: pointer; border: 0; background: transparent; padding: 0; border-radius: 5px; }
.coa-view .acc-name .chev:hover { color: var(--text); background: var(--surface-2); }
.coa-view .acc-name .chev-spacer { visibility: hidden; cursor: default; }
.coa-view .acc-name.collapsed .chev { transform: rotate(-90deg); }
.coa-view .acc-name.collapsed .chev:dir(rtl) { transform: rotate(90deg); }
.coa-view .acc-name .nm { font-weight: 600; }
.coa-view .row-group .cell { background: var(--surface-2); }
.coa-view .row-group .nm { font-weight: 800; }
.coa-view .row-group .code { color: var(--text-muted); }
.coa-view .row-group td { border-bottom: 1px solid var(--border); }
.coa-view .row-sub .nm { font-weight: 700; }
.coa-view tr.coa-row:hover .cell { background: color-mix(in oklab, var(--primary) 6%, var(--surface)); }
.coa-view .row-group:hover .cell { background: var(--surface-2); }

.coa-view .tbadge { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: var(--r-pill); font-size: 11px; font-weight: 700; }
.coa-view .cur-tag { font-family: var(--font-num); font-size: 11px; font-weight: 700; color: var(--text-muted); border: 1px solid var(--border); border-radius: 5px; padding: 1px 6px; }
.coa-view .status-pill { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: var(--r-pill); font-size: 11px; font-weight: 700; }
.coa-view .status-pill.on { background: color-mix(in oklab, var(--success) 16%, transparent); color: var(--success); }
.coa-view .status-pill.off { background: var(--surface-2); color: var(--text-subtle); }

.coa-view .acc-actions { opacity: 0; display: inline-flex; gap: 2px; margin-inline-start: 6px; transition: opacity .12s; }
.coa-view tr.coa-row:hover .acc-actions, .coa-view .acc-name:focus-within .acc-actions { opacity: 1; }
.coa-view .acc-actions button { width: 26px; height: 26px; border-radius: 6px; display: grid; place-items: center; color: var(--text-subtle); border: 0; background: transparent; cursor: pointer; }
.coa-view .acc-actions button:hover { background: var(--surface-2); color: var(--text); }
.coa-view .coa-empty { padding: 40px 16px; text-align: center; color: var(--text-muted); font-size: var(--fs-sm); }
`;
